export interface GeoAnchor {
  latitude: number
  longitude: number
  metersPerPixel: number
  northRotationDegrees: number
}

/**
 * Converts ReactFlow pixel coordinates to WGS84 lat/lng using the geo-anchor affine transform.
 *
 * The anchor defines the real-world coordinate of pixel origin (0, 0).
 * northRotationDegrees rotates the canvas: at 0° the canvas X-axis points east.
 * Screen Y increases downward, so positive py moves south.
 */
export function pixelToLatLng(
  px: number,
  py: number,
  anchor: GeoAnchor,
): { lat: number; lng: number } {
  const θ = (anchor.northRotationDegrees * Math.PI) / 180
  const eastMeters = anchor.metersPerPixel * (px * Math.cos(θ) - py * Math.sin(θ))
  const northMeters = -anchor.metersPerPixel * (px * Math.sin(θ) + py * Math.cos(θ))
  const δlng = eastMeters / (111320 * Math.cos((anchor.latitude * Math.PI) / 180))
  const δlat = northMeters / 111320
  return {
    lat: anchor.latitude + δlat,
    lng: anchor.longitude + δlng,
  }
}

/**
 * Computes the MapLibre zoom level that visually matches the given ReactFlow zoom.
 *
 * MapLibre GL JS uses an internal world size of 512 × 2^z CSS pixels (hardcoded in Transform).
 * At zoom z the ground resolution is: circumference × cos(lat) / (512 × 2^z) meters per CSS pixel.
 * We solve for z such that this matches metersPerPixel / rfZoom.
 */
export function calculateMapZoom(
  reactflowZoom: number,
  lat: number,
  metersPerPixel: number,
): number {
  const latRad = (lat * Math.PI) / 180
  const rawZoom = Math.log2(
    (40_075_016 * Math.cos(latRad) * reactflowZoom) / (512 * metersPerPixel),
  )
  return Math.max(0, Math.min(22, rawZoom))
}
